import {inject, Injectable} from '@angular/core';
import {BaseErrorService} from './base-error.service';
import {LogMessage} from '../../models/error/LogMessage';
import {WatcherHttpClient} from '../httpClient/WatcherHttpClient';
import {TenantService} from '../tenant/tenant.service';
import {forkJoin, map, Observable, of, switchMap} from 'rxjs';
import {ErrorFilter} from '../../models/filter/ErrorFilter';
import {ErrorLog} from '../../models/error/ErrorLog';
import {LogMessageAPI} from '../../api/models/error/LogMessageApi';
import { ErrorPriority } from '../../enums/ErrorPriority';

@Injectable({
    providedIn: 'root'
})
export class LogMessageService extends BaseErrorService<LogMessage> {
    protected readonly necessaryLogMessageFields: string[] = [
        "Description",
        "Content"
    ];

    private watcherHttpClient: WatcherHttpClient = inject(WatcherHttpClient);
    private tenantService: TenantService = inject(TenantService);

    public getAmountOfErrorItems(errorFilter: ErrorFilter): Observable<number> {
        return this.watcherHttpClient.get<number>("/logMessage/$count" + this.createODataQueryForAllErrorItems(errorFilter))
    }

    public updateErrorType(logMessage: LogMessage, newPriority: ErrorPriority): Observable<void> {
        return this.watcherHttpClient.patch<void>("/logMessage/" + logMessage.id, {
            "errorPriority": newPriority
        });
    }

    public solveError(logMessage: LogMessage): Observable<void> {
        return this.watcherHttpClient.patch<void>("/logMessage/" + logMessage.id, {
            "isSolved": true
        });
    }

    protected getAllWithFilter(filter: string): Observable<LogMessage[]> {
        return this.watcherHttpClient.get<LogMessageAPI[]>("/logMessage" + filter).pipe(
            switchMap((logMessages: LogMessageAPI[]) => logMessages?.length > 0 ?
                forkJoin(logMessages.map(logMessage => this.createLogMessage(logMessage))) : of([])
            )
        );
    }

    private createLogMessage(logMessageAPI: LogMessageAPI): Observable<LogMessage> {
        return forkJoin({
            tenant: this.tenantService.getTenant(logMessageAPI.tenantId),
            application: this.tenantService.getApplication(logMessageAPI.applicationId, logMessageAPI.tenantId)
        }).pipe(
            map(({tenant, application}) =>
                new LogMessage(
                    logMessageAPI.id,
                    tenant,
                    application,
                    logMessageAPI.mostRecentOccurred,
                    logMessageAPI.occurrenceCount,
                    logMessageAPI.errorPriority,
                    logMessageAPI.isSolved,
                    logMessageAPI.description,
                    logMessageAPI.content,
                    logMessageAPI.errorLogs?.map(errorLogApi => new ErrorLog(
                        errorLogApi.id,
                        errorLogApi.occurred
                    )),
                    logMessageAPI.solution
                )
            )
        );
    }

    protected override getNecessaryFieldsForErrors(): string[] {
        const necessaryFields: string[] = super.getNecessaryFieldsForErrors();
        necessaryFields.push(...this.necessaryLogMessageFields);
        return necessaryFields;
    }
}
